import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { questionService } from '../../../shared/data';
import type {
  CreateQuestionRequest,
  CreateQuestionResponse,
} from 'src/types/helios/Question';

export interface UseCreateQuestionResult {
  /**
   * Creates (publishes) a question. Returns the mutation promise — resolves
   * with the Helios {@link CreateQuestionResponse} (the real `questionId`) on
   * success, rejects on failure so the caller can keep the draft unpublished.
   * The hook records the raw error into `error` and reflects the in-flight
   * state in `isPublishing`; the caller awaits the returned promise.
   */
  readonly publish: (request: CreateQuestionRequest) => Promise<CreateQuestionResponse>;
  /** True while a create request is in flight. */
  readonly isPublishing: boolean;
  /** Raw error message from the last failed publish, or null. UI-agnostic —
   *  the consuming layer wraps it in localized copy. */
  readonly error: string | null;
  /** Clears the current error — call on retry or dismiss. */
  readonly clearError: () => void;
}

/**
 * Create-question mutation for the AuditWhiz review flow's "Save & publish".
 *
 * Calls the raw REST `questionService.create` (NOT the Smithy
 * `CreateQuestionCommand` — the question API returns epoch-number timestamps
 * that fail the typed client's RFC-3339 deserializer, so v2 uses the raw
 * service for questions). Mirrors the `useCreateTask` surface: a `publish`
 * action plus `isPublishing`/`error`/`clearError` for the caller's UI.
 *
 * Error/success recording lives in the mutation's `onError`/`onSuccess`
 * callbacks (no `await` in the hook — async orchestration is the caller's job);
 * `publish` just forwards the mutation promise for the caller to await.
 */
export function useCreateQuestion(): UseCreateQuestionResult {
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation<CreateQuestionResponse, Error, CreateQuestionRequest>({
    mutationFn: (request) => questionService.create(request),
    onMutate: () => setError(null),
    onError: (err: Error) => {
      // Raw message only — localized copy belongs to the component layer.
      setError(err.message || 'Failed to publish question');
    },
  });

  const { mutateAsync } = mutation;

  const publish = useCallback(
    (request: CreateQuestionRequest): Promise<CreateQuestionResponse> =>
      mutateAsync(request),
    [mutateAsync],
  );

  const clearError = useCallback(() => setError(null), []);

  return { publish, isPublishing: mutation.isPending, error, clearError };
}
